import React, { useContext, useState, useEffect } from "react";
import "./ContactForm.css";
import { ImageContext } from "../../Context/ImageContext";
import { CircularProgress } from "@mui/material";
import { applyPerspectiveWarp } from "../../../utils/newPerspectiveWarp";
import { generateGliderolFilename } from "../../../utils/fileNaming";
import { useAnalytics } from "../../../Hooks/useAnalytics";

const ContactForm = ({
  initialValues = {},
  series,
  design,
  color,
  style,
  pattern,
  doorwidth,
  onClose,
  // Optional props for high-res image generation
  points,
  doorImage,
  bgImage,
  houseImageDimensions,
}) => {
  const [formData, setFormData] = useState(initialValues);
  const [status, setStatus] = useState("");
  const { imgurl } = useContext(ImageContext);
  const [loading, setLoading] = useState(false);
  const [recaptchaLoaded, setRecaptchaLoaded] = useState(false);
  const [formStarted, setFormStarted] = useState(false);
  const { trackEvent, trackFormSubmit } = useAnalytics();

  useEffect(() => {
    const checkRecaptcha = () => {
      if (window.grecaptcha && window.grecaptcha.ready) {
        window.grecaptcha.ready(() => {
          setRecaptchaLoaded(true);
        });
      } else {
        setTimeout(checkRecaptcha, 100);
      }
    };
    checkRecaptcha();
  }, []);

  const getRecaptchaToken = async () => {
    if (!recaptchaLoaded || !window.grecaptcha) {
      throw new Error('reCAPTCHA not loaded');
    }
    
    const siteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6Le-XGIrAAAAAO0CVRyTzGepFgj_LfB8l67R7kGE';
    if (!siteKey) {
      throw new Error('reCAPTCHA site key not configured');
    }
    
    try {
      const token = await window.grecaptcha.execute(siteKey, {
        action: 'contact_form_submit'
      });
      
      if (!token) {
        throw new Error('Failed to generate reCAPTCHA token');
      }
      
      return token;
    } catch (error) {
      console.error('reCAPTCHA execution error:', error);
      throw new Error('reCAPTCHA verification failed');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    
    // Track form start on first interaction
    if (!formStarted) {
      setFormStarted(true);
      
      // Track form start event
      trackEvent('quote_form_start', {
        form_door_style: pattern || 'Not specified',
        form_door_series: series || 'Not specified',
        form_door_design: design || 'Not specified',
        form_door_colour: color || 'Not specified',
        form_source: 'visualiser'
      });
    }
  };

  // Simple function to generate 1200px image
  const generate1200pxImage = async () => {
    // Try to get background image from DOM if not provided
    let backgroundImageSrc = bgImage;
    if (!backgroundImageSrc) {
      const bgImageElement = document.querySelector('#image-display-area img');
      if (bgImageElement) {
        backgroundImageSrc = bgImageElement.src;
      }
    }

    // If we still don't have required data, use preview image
    if (!points || !doorImage || !backgroundImageSrc) {
      console.log('Missing required data, using preview image');
      return imgurl;
    }

    try {
      // Get container dimensions
      const containerElement = document.querySelector('#image-display-area');
      const containerWidth = containerElement ? containerElement.getBoundingClientRect().width : 840;
      const containerHeight = containerElement ? containerElement.getBoundingClientRect().height : 580;

      console.log('Generating 1200px image...');
      console.log('Container size:', containerWidth, 'x', containerHeight);

      // Create 1200px canvas
      const canvas = document.createElement("canvas");
      const targetWidth = 1200;
      
      // Calculate height maintaining aspect ratio
      let targetHeight = 800; // default
      if (houseImageDimensions) {
        const aspectRatio = houseImageDimensions.height / houseImageDimensions.width;
        targetHeight = Math.round(targetWidth * aspectRatio);
      }

      canvas.width = targetWidth;
      canvas.height = targetHeight;
      const ctx = canvas.getContext("2d");

      // Enable high-quality rendering
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';

      console.log('Canvas created:', targetWidth, 'x', targetHeight);

      // Load images
      const bg = new Image();
      bg.crossOrigin = "anonymous";
      bg.src = backgroundImageSrc;

      const door = new Image();
      door.crossOrigin = "anonymous";
      door.src = doorImage;

      await Promise.all([
        new Promise((resolve, reject) => {
          bg.onload = resolve;
          bg.onerror = reject;
        }),
        new Promise((resolve, reject) => {
          door.onload = resolve;
          door.onerror = reject;
        }),
      ]);

      // Draw background at 1200px
      ctx.drawImage(bg, 0, 0, targetWidth, targetHeight);

      // Scale points to 1200px canvas
      const scaleX = targetWidth / containerWidth;
      const scaleY = targetHeight / containerHeight;
      
      const scaledPoints = points.map(point => ({
        x: point.x * scaleX,
        y: point.y * scaleY
      }));

      console.log('Scale factors:', scaleX, scaleY);
      console.log('Scaled points:', scaledPoints);

      // Use your existing sophisticated perspective warp function
      if (scaledPoints.length >= 4) {
        const doorSourcePoints = [
          { x: 0, y: 0 },
          { x: door.naturalWidth, y: 0 },
          { x: door.naturalWidth, y: door.naturalHeight },
          { x: 0, y: door.naturalHeight },
        ];

        console.log('Using applyPerspectiveWarp with:');
        console.log('From points (door corners):', doorSourcePoints);
        console.log('To points (scaled):', scaledPoints);

        // Use the same pattern as your existing code
        applyPerspectiveWarp({
          ctx,
          img: door,
          from: doorSourcePoints,
          to: scaledPoints,
        });
      }

      console.log('1200px image generated successfully!');
      return canvas.toDataURL("image/jpeg", 0.95);

    } catch (error) {
      console.error('Error generating 1200px image:', error);
      return imgurl; // Fallback to preview
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus("Generating high-resolution image...");
    
    const submissionStartTime = Date.now();
    
    try {
      // Generate 1200px image
      const imageDataUrl = await generate1200pxImage();
      
      setStatus("Verifying request...");
      
      // Get reCAPTCHA token
      let recaptchaToken;
      try {
        recaptchaToken = await getRecaptchaToken();
        console.log('reCAPTCHA token generated successfully');
      } catch (recaptchaError) {
        console.error('reCAPTCHA error details:', recaptchaError);
        setStatus("❌ Security verification failed. Please refresh the page and try again.");
        setLoading(false);
        return;
      }
      
      setStatus("Sending your request...");

      // Convert to base64 for email attachment
      const response = await fetch(imageDataUrl);
      const blob = await response.blob();
      const arrayBuffer = await blob.arrayBuffer();
      const u8arr = new Uint8Array(arrayBuffer);
      
      let binary = "";
      for (let i = 0; i < u8arr.byteLength; i++) {
        binary += String.fromCharCode(u8arr[i]);
      }
      const base64String = btoa(binary);

      // Build the payload
      const payload = {
        // reCAPTCHA token
        recaptchaToken,
        
        // Customer details
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        street: formData.street,
        suburb: formData.suburb,
        postcode: formData.postcode,
        note: formData.note || "No additional notes",
        
        // Appointment details
        appointmentDay: formData.appointmentDay || "Not specified",
        appointmentTime: formData.appointmentTime || "Not specified",
        
        // Door specifications
        doorWidth: doorwidth || "Not specified", 
        doorStyle: pattern || "Not specified",   
        panelStyle: style || "Not specified",    
        series: series || "Not specified",
        doorDesign: design || "Not specified",
        doorColour: color || "Not specified",
        
        // Image data
        imagePath: imageDataUrl,
        attachments: [
          {
            name: generateGliderolFilename(),
            content: base64String,
            contentType: "image/jpeg",
          },
        ],
      };

      // Submit to backend
      const res = await fetch("/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      
      if (res.ok) {
        const submissionTime = Date.now() - submissionStartTime;
        
        // Track quote request submission
        trackFormSubmit('quote_request', {
          quote_door_style: pattern || 'Not specified',
          quote_door_series: series || 'Not specified',
          quote_door_design: design || 'Not specified',
          quote_door_colour: color || 'Not specified',
          quote_door_width: doorwidth || 'Not specified',
          quote_panel_style: style || 'Not specified',
          quote_customer_postcode: formData.postcode || 'Not specified',
          quote_submission_time_ms: submissionTime,
          quote_source: 'visualiser'
        });
        
        setStatus("✅ Request sent successfully! We'll contact you soon.");
        setFormData({});
        setTimeout(() => {
          if (onClose) onClose();
        }, 4000);
      } else {
        const errorText = await res.text();
        console.error('Server response:', res.status, errorText);
        
        // Handle form errors
        if (res.status === 403) {
          setStatus("❌ Security verification failed. Please refresh the page and try again.");
        } else if (res.status === 400) {
          setStatus("❌ Please check your information and try again.");
        } else {
          setStatus("❌ Server error. Please try again or contact us directly.");
        }
        throw new Error(`Server error: ${res.status} - ${errorText}`);
      }
      
    } catch (err) {
      console.error("Form submission error:", err);
      setStatus("❌ Failed to send request. Please try again or contact us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <h3 style={{ marginBottom: "1rem", color: "#17376c" }}>
        Request Your Free Quote
      </h3>
      
      {/* Customer Information */}
      <div className="form-section">
        <h4>Contact Details</h4>
        <input
          name="name"
          placeholder="Full Name *"
          value={formData.name || ""}
          onChange={handleChange}
          required
        />
        <input
          name="phone"
          placeholder="Phone Number *"
          value={formData.phone || ""}
          onChange={handleChange}
          required
        />
        <input
          name="email"
          type="email"
          placeholder="Email Address *"
          value={formData.email || ""}
          onChange={handleChange}
          required
        />
      </div>

      {/* Address Information */}
      <div className="form-section">
        <h4>Installation Address</h4>
        <input
          name="street"
          placeholder="Street Address *"
          value={formData.street || ""}
          onChange={handleChange}
          required
        />
        <input
          name="suburb"
          placeholder="Suburb"
          value={formData.suburb || ""}
          onChange={handleChange}
        />
        <input
          name="postcode"
          placeholder="Postcode *"
          value={formData.postcode || ""}
          onChange={handleChange}
          pattern="[0-9]{4}"
          title="Please enter a valid 4-digit postcode"
          required
        />
      </div>

      {/* Appointment Preferences */}
      <div className="form-section">
        <h4>Preferred Appointment Time</h4>
        <select
          name="appointmentDay"
          value={formData.appointmentDay || ""}
          onChange={handleChange}
        >
          <option value="">Preferred Day...</option>
          <option value="Monday">Monday</option>
          <option value="Tuesday">Tuesday</option>
          <option value="Wednesday">Wednesday</option>
          <option value="Thursday">Thursday</option>
          <option value="Friday">Friday</option>
          <option value="Saturday">Saturday</option>
        </select>
        <select
          name="appointmentTime"
          value={formData.appointmentTime || ""}
          onChange={handleChange}
        >
          <option value="">Preferred Time...</option>
          <option value="Morning (8AM-12PM)">Morning (8AM-12PM)</option>
          <option value="Afternoon (12PM-5PM)">Afternoon (12PM-5PM)</option>
          <option value="Any time">Any time</option>
        </select>
      </div>

      {/* Additional Notes */}
      <textarea
        name="note"
        placeholder="Additional notes or requirements..."
        value={formData.note || ""}
        onChange={handleChange}
        rows="3"
      />

      {/* Door Specifications - Read Only */}
      <div className="form-section">
        <h4>Your Door Configuration</h4>
        <input
          name="doorWidth"
          value={doorwidth || "Door Width not specified"}
          readOnly
          className="readonly-input"
          title="Door Width"
        />
        <input
          name="doorStyle" 
          value={pattern || "Door Style not specified"}
          readOnly
          className="readonly-input"
          title="Door Style"
        />
        <input
          name="series"
          value={series || "Series not specified"}
          readOnly
          className="readonly-input"
          title="Door Series"
        />
        <input
          name="doorDesign"
          value={design || "Door Design not specified"}
          readOnly
          className="readonly-input"
          title="Door Design"
        />
        <input
          name="doorColour"
          value={color || "Door Colour not specified"}
          readOnly
          className="readonly-input"
          title="Door Colour"
        />
        {style && (
          <input
            name="panelStyle"
            value={style || "Panel Style not specified"}
            readOnly
            className="readonly-input"
            title="Panel Style"
          />
        )}
      </div>

      {/* Hidden fields */}
      <input name="imagePath" value={imgurl || ""} type="hidden" />

      {/* Preview image (optional) */}
      {imgurl && (
        <div className="image-preview" style={{ marginBottom: "1rem" }}>
          <p style={{ fontSize: "0.9rem", color: "#666" }}>Your Design Preview:</p>
          <img 
            src={imgurl} 
            alt="Door design preview"
            style={{
              maxWidth: "100%", 
              height: "auto", 
              border: "1px solid #ddd",
              borderRadius: "4px"
            }}
          />
        </div>
      )}

      {/* Submit Button */}
      <button type="submit" disabled={loading}>
        {loading ? (
          <span style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
            <CircularProgress size={16} color="inherit" />
            Sending Request...
          </span>
        ) : (
          "REQUEST YOUR FREE QUOTE"
        )}
      </button>

      {/* Status Messages */}
      {status && (
        <div className={`status-message ${
          status.includes("Generating") ? "generating" :
          status.includes("Verifying") ? "verifying" :
          status.includes("Sending") ? "sending" :
          status.includes("✅") ? "success" : "error"
        }`}>
          {status}
        </div>
      )}
    </form>
  );
};

export default ContactForm;